/* eslint-disable react/prop-types */
import * as React from "react";
import cs from "classnames";

import { useLaunchDarklyFlag } from "@lululemon/mwa-launch-darkly";

import {
  analyticsProductLinkDataAttributes,
  trackEvent,
  trackCarouselProductTileClick,
} from "@lululemon/ecom-common/src/lib/analytics";

import { getUsrLocaleStandard } from "@lululemon/ecom-common/src/util/locale-utils";
import { publicConfig } from "@lululemon/ecom-common/src/util/public-config";

import {
  ProductCarousel,
  ProductTile,
  GridContainer,
} from "@lululemon/ecom-pattern-library";

import useRecommenders from "./useRecommenders";

import styles from "./recommenders-carousel.module.scss";

const RecommendersCarousel = ({
  isHomeRecsEnabled,
  aiCampaignEnabled,
  recsOverrideUrl,
  children,
  recKey = "",
  // campaignId = "ai-recommender",
  homepage = false,
  collectionType,
  graphqlClient,
  shouldUseGraphQL,
  ...CMSProps
}) => {
  const homepageAiCampaign = useLaunchDarklyFlag(
    "homepage-ai-campaign",
    { enabled: false },
    publicConfig?.launchDarkly?.clientSideId
  );

  const isAICampaignEnabled =
    aiCampaignEnabled && homepageAiCampaign?.enabled && homepage;
  const {
    recUrl,
    campaignId,
    callToAction,
    callToActionWebRoute,
    carouselTitle,
    analyticsTitle,
  } = React.useMemo(() => {
    const locale = getUsrLocaleStandard();
    const callToActionWebRoute =
      locale === "en-US"
        ? homepageAiCampaign?.callToActionUS || "/c/gift-ideas/_/N-8mq"
        : homepageAiCampaign?.callToActionCA || "/en-ca/c/gift-ideas/_/N-8mp";
    const recUrl =
      (homepageAiCampaign?.recUrl ||
        "/api/r/products?type=itemToItems&itemId=gifting&dataSource=datascience&modelId=CatTrendingGiftingV1") +
      `&locale=${locale.replace("-", "_")}`;
    return {
      campaignId: isAICampaignEnabled
        ? homepageAiCampaign.analyticsCampaignId || "ai-recommender"
        : "",
      callToAction: isAICampaignEnabled
        ? homepageAiCampaign.callToActionLabel || "SHOP GIFTS FOR ALL"
        : "Shop Handpicked Just For You",
      carouselTitle: isAICampaignEnabled
        ? homepageAiCampaign.carouselTitle || "Trending gifts: present."
        : "Handpicked just for you",
      analyticsTitle: "product-list-tpfy-data-science",
      callToActionWebRoute: isAICampaignEnabled
        ? callToActionWebRoute
        : "/pcdp",
      recUrl: (isAICampaignEnabled && recUrl) || recsOverrideUrl,
    };
  }, [isAICampaignEnabled]);

  const isHomepageRecommender = isHomeRecsEnabled || isAICampaignEnabled;
  const [isFallback, setIsFallback] = React.useState(true);
  const { recData, ecid } = useRecommenders({
    isHomeRecsEnabled: isHomepageRecommender,
    recsOverrideUrl: recUrl,
    graphqlClient,
    shouldUseGraphQL,
    aiCampaignConfig: isAICampaignEnabled ? homepageAiCampaign : null,
  });
  const { items } = recData?.included?.[0] || [];
  const hasError = recData?.hasError;

  const ctaWebRoute = React.useMemo(
    () =>
      isAICampaignEnabled
        ? `${callToActionWebRoute}${
            callToActionWebRoute.indexOf("?") === -1 ? "?" : "&"
          }icid=carousel:${campaignId}`
        : callToActionWebRoute,
    [callToActionWebRoute, isAICampaignEnabled, campaignId]
  );

  React.useEffect(() => {
    if (!recData?.data?.[0].attributes || !isHomepageRecommender) return;
    setIsFallback(recData?.data?.[0].attributes.recFallback);
  }, [recData, isHomepageRecommender]);

  React.useEffect(() => {
    if (!isFallback) {
      trackEvent("ECID-match-detected", {
        id: [ecid],
      });
    }
  }, [ecid, isFallback]);

  if (hasError) return null;

  if (!isHomepageRecommender || isFallback) {
    return <GridContainer layout="1 Column - Wide">{children}</GridContainer>;
  }
  return (
    <GridContainer layout="1 Column - Wide" key={recKey}>
      {!recData?.included ? (
        <div
          className={cs("skeleton-loading", styles.recsPlaceholder)}
          data-testid="rec-loading"
        />
      ) : (
        <ProductCarousel
          title={carouselTitle}
          callToAction={callToAction}
          webRoute={ctaWebRoute}
        >
          {items?.map((item, index) => {
            const nameWithoutAsterisk = (item["display-name"] || "").replace(
              /\s*\*\s*/,
              " "
            );
            const styleID =
              item?.["sku-style-order"]?.[0]?.["style-id"]?.split("-")?.[0] ||
              "";
            const hasSalePrice = item?.["sale-price"].length > 0;
            const prices =
              (hasSalePrice ? item?.["sale-price"] : item?.["list-price"]) ||
              "";
            const skuStyle = item?.["sku-style-order"]?.[0] || "";
            const analyticsProps = {};
            if (isAICampaignEnabled) {
              analyticsProps.onClick = () =>
                trackCarouselProductTileClick({
                  campaignId,
                  carouselType: "RecommendersCarousel",
                  product: item,
                  products: items,
                });
            } else {
              analyticsProps.analyticsAttributes =
                analyticsProductLinkDataAttributes({
                  product: item,
                  skuStyle,
                  productIndex: index + 1,
                  overrides: {
                    component: analyticsTitle,
                    dataLuluTrack: "homepage-recommendations-pdp-link",
                    productId: item?.["product-id"] || "",
                    styleID,
                    size: "",
                    quantity: "",
                    price: prices?.[0] || "",
                    version: "2.3",
                  },
                });
            }
            return (
              <ProductTile
                href={item["pdp-url"]}
                imageProps={{
                  src: item["sku-sku-images"][0],
                }}
                name={nameWithoutAsterisk}
                priceProps={{
                  prices,
                }}
                key={`${item["product-id"]}-recs-carousel`}
                {...analyticsProps}
              />
            );
          })}
        </ProductCarousel>
      )}
    </GridContainer>
  );
};

export default RecommendersCarousel;
